import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Pin, Megaphone, Eye, EyeOff } from 'lucide-react';
import { Switch } from '../ui/switch';
import { API, getAuthHeaders } from '../../utils/adminAuth';

/**
 * Admin Announcements management.
 *
 * The owner broadcasts here; ambassadors see pinned ones at the top of
 * their dashboard and the full feed inside the notifications bell. We
 * intentionally keep the body field plain-text (with basic newline
 * preservation in the consumer) — markdown adds attack surface and the
 * owner uses Taglish more than formatting anyway.
 */
export default function AnnouncementsTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/announcements`, { headers: getAuthHeaders() });
      setItems(r.data || []);
    } catch { toast.error('Failed to load announcements'); }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const newForm = () => {
    setEditing(null);
    setForm({ title: '', body: '', audience: 'ambassadors', is_pinned: false, is_active: true, expires_at: '' });
  };

  const edit = (a) => {
    setEditing(a);
    setForm({
      title: a.title,
      body: a.body,
      audience: a.audience || 'ambassadors',
      is_pinned: !!a.is_pinned,
      is_active: a.is_active !== false,
      expires_at: a.expires_at ? a.expires_at.slice(0, 10) : '',
    });
  };

  const save = async () => {
    if (!form.title.trim() || !form.body.trim()) { toast.error('Title and body required'); return; }
    try {
      const payload = {
        ...form,
        expires_at: form.expires_at ? new Date(form.expires_at + 'T23:59:59').toISOString() : null,
      };
      if (editing) {
        await axios.put(`${API}/admin/announcements/${editing.id}`, payload, { headers: getAuthHeaders() });
        toast.success('Announcement updated');
      } else {
        await axios.post(`${API}/admin/announcements`, payload, { headers: getAuthHeaders() });
        toast.success('Announcement published');
      }
      setForm(null);
      setEditing(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
  };

  const del = async (a) => {
    if (!window.confirm(`Delete "${a.title}"?`)) return;
    try {
      await axios.delete(`${API}/admin/announcements/${a.id}`, { headers: getAuthHeaders() });
      toast.success('Deleted');
      load();
    } catch { toast.error('Delete failed'); }
  };

  return (
    <div data-testid="announcements-tab" className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <p className="text-xs uppercase tracking-[0.22em] text-[#D4AF37] font-sans">Broadcast Channel</p>
          <h2 className="text-xl text-white" style={{ fontFamily: 'Cormorant Garamond, serif' }}>Announcements</h2>
          <p className="text-white/40 text-xs font-sans mt-1">
            Goes straight to your ambassadors' dashboard. Pinned items get a golden glow until they tap to dismiss.
          </p>
        </div>
        <button onClick={newForm} data-testid="ann-new-btn"
          className="flex items-center gap-1.5 px-4 py-2 bg-[#D4AF37] text-black text-xs uppercase tracking-[0.18em] font-medium hover:bg-[#E5C158] transition-colors">
          <Plus className="w-3.5 h-3.5" /> New Announcement
        </button>
      </div>

      {form && (
        <div className="bg-[#141414] border border-[#D4AF37]/30 p-5 space-y-3" data-testid="ann-form">
          <input type="text" placeholder="Title (e.g., 🔥 New collection drop tomorrow!)"
            value={form.title} onChange={e => setForm({ ...form, title: e.target.value })}
            className="w-full bg-[#0A0A0A] border border-white/10 px-3 py-2 text-sm text-white focus:border-[#D4AF37] focus:outline-none"
            data-testid="ann-title-input" />
          <textarea placeholder="Body — supports line breaks. Speak directly to your team."
            value={form.body} onChange={e => setForm({ ...form, body: e.target.value })} rows={5}
            className="w-full bg-[#0A0A0A] border border-white/10 px-3 py-2 text-sm text-white focus:border-[#D4AF37] focus:outline-none resize-none font-sans"
            data-testid="ann-body-input" />
          <div className="grid sm:grid-cols-3 gap-3">
            <div>
              <label className="text-white/40 text-[10px] uppercase tracking-wider font-sans block mb-1">Audience</label>
              <select value={form.audience} onChange={e => setForm({ ...form, audience: e.target.value })}
                className="w-full bg-[#0A0A0A] border border-white/10 px-3 py-2 text-sm text-white"
                data-testid="ann-audience-select">
                <option value="ambassadors">Ambassadors only</option>
                <option value="all">All ambassadors</option>
              </select>
            </div>
            <div>
              <label className="text-white/40 text-[10px] uppercase tracking-wider font-sans block mb-1">Expires (optional)</label>
              <input type="date" value={form.expires_at} onChange={e => setForm({ ...form, expires_at: e.target.value })}
                className="w-full bg-[#0A0A0A] border border-white/10 px-3 py-2 text-sm text-white"
                data-testid="ann-expires-input" />
            </div>
            <div className="flex items-end gap-4">
              <label className="flex items-center gap-2 text-xs text-white/60 font-sans">
                <Switch checked={form.is_pinned} onCheckedChange={v => setForm({ ...form, is_pinned: v })} data-testid="ann-pin-toggle" />
                <Pin className="w-3.5 h-3.5 text-[#D4AF37]" /> Pin
              </label>
              <label className="flex items-center gap-2 text-xs text-white/60 font-sans">
                <Switch checked={form.is_active} onCheckedChange={v => setForm({ ...form, is_active: v })} data-testid="ann-active-toggle" />
                Active
              </label>
            </div>
          </div>
          <div className="flex gap-2 justify-end pt-2">
            <button onClick={() => { setForm(null); setEditing(null); }}
              className="px-4 py-2 text-xs uppercase tracking-[0.18em] border border-white/15 text-white/60 hover:text-white">Cancel</button>
            <button onClick={save} data-testid="ann-save-btn"
              className="px-4 py-2 text-xs uppercase tracking-[0.18em] bg-[#D4AF37] text-black font-medium hover:bg-[#E5C158] transition-colors">
              {editing ? 'Update' : 'Publish'}
            </button>
          </div>
        </div>
      )}

      <div className="space-y-2" data-testid="ann-list">
        {loading && <p className="text-white/30 text-sm font-sans py-8 text-center bg-[#141414]">Loading…</p>}
        {!loading && items.length === 0 && (
          <div className="bg-[#141414] border border-white/5 p-12 text-center">
            <Megaphone className="w-8 h-8 text-white/15 mx-auto mb-2" />
            <p className="text-white/40 text-sm font-sans">No announcements yet. Hit "New Announcement" to broadcast.</p>
          </div>
        )}
        {items.map(a => (
          <div key={a.id} className={`bg-[#141414] border p-4 ${a.is_pinned ? 'border-[#D4AF37]/40' : 'border-white/5'}`}
            data-testid={`ann-row-${a.id}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap mb-1">
                  {a.is_pinned && <Pin className="w-3.5 h-3.5 text-[#D4AF37]" />}
                  <h3 className="text-white text-sm font-medium font-sans">{a.title}</h3>
                  <span className={`text-[10px] uppercase px-1.5 py-0.5 font-sans ${a.is_active ? 'bg-[#1C7D3F]/20 text-[#2E9B50]' : 'bg-white/5 text-white/40'}`}>
                    {a.is_active ? 'Active' : 'Hidden'}
                  </span>
                  {a.expires_at && (
                    <span className="text-[10px] uppercase text-white/40 font-sans">
                      Expires {new Date(a.expires_at).toLocaleDateString('en-PH')}
                    </span>
                  )}
                </div>
                <p className="text-white/60 text-xs font-sans whitespace-pre-line line-clamp-3">{a.body}</p>
                <p className="text-white/30 text-[10px] font-sans mt-2">
                  Posted {new Date(a.created_at).toLocaleString('en-PH')}
                </p>
              </div>
              <div className="flex gap-1 flex-shrink-0">
                <button onClick={() => edit(a)} className="p-1.5 text-white/40 hover:text-[#D4AF37]" data-testid={`ann-edit-${a.id}`}>
                  <Pencil className="w-3.5 h-3.5" />
                </button>
                <button onClick={() => del(a)} className="p-1.5 text-white/40 hover:text-red-400" data-testid={`ann-del-${a.id}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="text-white/30 text-[11px] font-sans flex items-center gap-1.5 pt-2">
        <Eye className="w-3 h-3" /> Active + within expiry → shows to ambassadors
        <span className="mx-2">·</span>
        <EyeOff className="w-3 h-3" /> Inactive → hidden from ambassadors
      </div>
    </div>
  );
}
